'use client';

import clsx from 'clsx';
import { useState } from 'react';

import { DataTestIds } from '@repo/frontend-shared';
import { SDSDefaultAvatar } from '@repo/sds/assets/icons/SDSDefaultAvatar';
import { SDSSkeleton } from '@repo/sds/components/_internal/SDSSkeleton';
import { SDSCircularImage } from '@repo/sds/components/SDSCircularImage';
import type { SDSTextType } from '@repo/sds/components/SDSText';
import { SDSText } from '@repo/sds/components/SDSText';
import { getInitials } from '@repo/sds/utils/get-initials';

import '@repo/sds/components/SDSAvatar/SDSAvatar.scss';

/**
 * @type SDSAvatarSize
 * Defines the allowed size variations for the Avatar component.
 * These typically correspond to predefined CSS variables controlling width and height.
 */
export type SDSAvatarSize = 'tiny' | 'small' | 'medium' | 'big' | 'large' | 'xLarge';

/**
 * @interface AvatarProps
 * Props for the main SDSAvatar component. Configures the appearance and content,
 * including image source, fallback text/initials, size, and loading state.
 */
interface AvatarProps {
  /** The URL for the user's avatar image. If null or undefined, the component will attempt fallbacks. */
  src?: string | null;
  /**
   * Alt text for the avatar image. Crucial for accessibility.
   * If `name` is provided and `alt` is not, `name` might be used as a fallback `alt` internally.
   * A generic description like "User Avatar" is the final fallback.
   */
  alt?: string;
  /**
   * The user's name. Used to generate initials as a fallback mechanism when `src` is unavailable or fails to load.
   */
  name?: string;
  /**
   * Specifies the size of the avatar. Defaults to 'medium'.
   * This controls the overall dimensions and influences the text size used for initials.
   */
  size?: SDSAvatarSize;
  /** If true, displays a skeleton loader, overriding any other content. Useful during initial data fetch. */
  isLoading?: boolean;
  /** Test identifier for automation. */
  dataTestId?: string;
  /** Allows applying external CSS classes to the root avatar `div` for customization. */
  className?: string;
}

/**
 * @interface AvatarContentProps
 * Internal props specifically for the `SDSAvatarContent` component.
 * It inherits most props from `AvatarProps` but makes `size` mandatory,
 * as the content renderer requires a definite size value (which the parent `SDSAvatar` guarantees
 * through defaulting) to determine styling, particularly for initials text size.
 * It omits props handled solely by the parent wrapper (`dataTestId`, `className`).
 */
interface AvatarContentProps extends Omit<AvatarProps, 'size' | 'dataTestId' | 'className'> {
  size: SDSAvatarSize; // Size becomes required internally.
}

/**
 * @type InitialsSize
 * A mapping configuration that links each `SDSAvatarSize` variant to a specific
 * `SDSTextType`. This ensures the initials displayed within the avatar are scaled
 * appropriately according to the avatar's overall size, maintaining design consistency.
 */
type InitialsSize = { [key in SDSAvatarSize]: SDSTextType };
const initialsTextSize: InitialsSize = {
  tiny: 'button-small',
  xLarge: 'title',
  large: 'h5',
  big: 'h5',
  medium: 'body-large-bold',
  small: 'button-medium',
};

/**
 * Rendered slot width per avatar size (the `--size-avatar-*` tokens; x-large is 380px at its widest).
 * Without it, responsive image adapters assume a full-viewport slot and fetch a 1200px+ source for a 20px face.
 */
const AVATAR_IMAGE_SIZES: { [key in SDSAvatarSize]: string } = {
  tiny: '20px',
  small: '32px',
  medium: '40px',
  big: '48px',
  large: '56px',
  xLarge: '380px',
};

/**
 * @component SDSAvatarContent
 * Internal component responsible for rendering the actual visual content of the avatar
 * based on the provided props and internal state (like image loading errors).
 * It implements the fallback logic: Skeleton > Image > Initials > Default Icon.
 * Manages the `imageError` state internally, derived from the `onError` callback of the `SDSImage`.
 */
function SDSAvatarContent({ src, alt, name, isLoading, size }: AvatarContentProps) {
  return (
    <SDSAvatarImageContent key={src ?? 'fallback'} src={src} alt={alt} name={name} isLoading={isLoading} size={size} />
  );
}

function SDSAvatarImageContent({ src, alt, name, isLoading, size }: AvatarContentProps) {
  // State to track if the attempt to load the `src` image resulted in an error.
  const [imageError, setImageError] = useState(false);

  // Callback passed to the underlying `SDSImage` component.
  // It updates the internal `imageError` state when `SDSImage` signals a loading failure.
  const handleImageError = () => {
    setImageError(true);
  };

  // --- Content Rendering Priority Logic ---

  // 1. Loading State: Highest priority. If `isLoading` is true, show the skeleton overlay.
  if (isLoading) {
    return <SDSSkeleton height="100%" width="100%" radius="full" />;
  }

  // 2. Image State: If `src` is provided and no `imageError` has occurred, attempt to render the image
  //    using the `SDSCircularImage` component. `SDSCircularImage` handles its own internal loading/fallback states,
  //    but `onError` is hooked here to trigger the Avatar's own fallback logic (initials/default).
  if (src && !imageError) {
    return (
      <SDSCircularImage
        dataTestId={DataTestIds.COMMON.AVATAR_IMAGE}
        src={src}
        // Ensure `SDSCircularImage` always receives a meaningful alt text.
        alt={alt ?? name ?? 'User Avatar'}
        sizes={AVATAR_IMAGE_SIZES[size]}
        onError={handleImageError} // Connect image errors back to avatar state.
      />
    );
  }

  // 3. Initials State: If `name` is provided, and the image wasn't shown (either because `src`
  //    was absent or because `imageError` is true), render the user's initials.
  //    The `initials-wrapper` div handles the background color and centering.
  const ariaLabel = alt ?? name ?? 'User Avatar';

  if (name) {
    return (
      // This wrapper isolates the styling (background, centering) for the initials state.
      <div className="SDSAvatar__initials-wrapper">
        <span aria-hidden="true">
          <SDSText
            dataTestId={DataTestIds.COMMON.AVATAR_INITIALS}
            // Dynamically select the text style based on the avatar's size.
            type={initialsTextSize[size]}
            text={getInitials(name)} // Utility function generates initials from the name.
            color="on-brand" // Assumes initials text should contrast with the brand background.
          />
        </span>
        <span className="SDSAvatar__accessible-label">{ariaLabel}</span>
      </div>
    );
  }

  // 4. Default Fallback State: If none of the above conditions are met (no loading, no src/image, no name),
  //    render a generic default avatar icon.
  return (
    <div className="SDSAvatar__default-wrapper">
      <span aria-hidden="true">
        <SDSDefaultAvatar data-testid={DataTestIds.COMMON.DEFAULT_AVATAR} />
      </span>
      <span className="SDSAvatar__accessible-label">{ariaLabel}</span>
    </div>
  );
}

/**
 * @component SDSAvatar
 * Displays a user avatar, handling various states including image loading,
 * error fallbacks (initials or default icon), and skeleton loading placeholders.
 * It acts as the main entry point, controlling the overall size and structure,
 * and delegates the specific content rendering to `SDSAvatarContent`.
 */
export function SDSAvatar({ src, alt, size = 'medium', name, isLoading, dataTestId, className }: AvatarProps) {
  const avatarClassName = clsx('SDSAvatar', `SDSAvatar--${size}`, className);

  return (
    // The root element applies the base and size styles, and acts as the container.
    <div data-testid={dataTestId ?? DataTestIds.SDS.SDS_AVATAR} className={avatarClassName}>
      {/* Render the internal content, passing down all relevant props.
          `size` is guaranteed to be non-optional here due to the default value above. */}
      <SDSAvatarContent src={src} alt={alt} name={name} isLoading={isLoading} size={size} />
    </div>
  );
}
